import { Box, Divider, Stack, Tooltip } from "@mui/material";
import { Params } from "./Params";
import { dateTimeConversionService } from "../../../../shared/services/DateTimeConversionService";
import { DataProviders } from "../../../../shared/types/DataProviders";
import { useAppContext } from "../../../../shared/contexts/app-context/components/AppContext";
import { WebSocketHealthStateDto } from "../../../../shared/types/WebSocketHealthStateDto";

export function StatusInfoPanel({
    lastDataUpdateTimestamp,
    isDataUpdating,
    spotsConnection,
    futuresConnection,
    optionsConnection,
    isOptionsOrderBookEnabled,
    optionsOrderBookConnections,
}: Params) {
    const {
        selectedDataProvider,
    } = useAppContext();
    
    const dataProviderName = getDataProviderName(selectedDataProvider);
    const optionsOrderBookConnectionList = isOptionsOrderBookEnabled
        ? getSortedOptionsOrderBookConnections(optionsOrderBookConnections)
        : [];
    const streamConnectionDetails = getStreamConnectionDetails(
        spotsConnection,
        futuresConnection,
        optionsConnection,
        optionsOrderBookConnectionList,
        isOptionsOrderBookEnabled);
    const currentInfoColor = isDataUpdating || lastDataUpdateTimestamp === null
        ? '#ff5050'
        : !isDataUpdating && selectedDataProvider === DataProviders.Snapshot
            ? 'rgba(226,255,219,0.67)'
            : undefined;
    
    return (
        <Stack
            direction="row"
            justifyContent="flex-end" 
            alignItems="flex-end"
            spacing={2}
            sx={{
                fontSize: 14,
                margin: '0px',
                padding: '0px',
                lineHeight: 1.25,
                whiteSpace: 'nowrap',
            }}
        >
            <Stack
                direction="column"
                sx={{
                    fontWeight: "500",
                    ...(currentInfoColor ? { color: currentInfoColor } : {}),
                }}
            >
                <Stack direction="row">
                    <Box width="65px">Provider:</Box>
                    <Box>{dataProviderName}</Box>
                </Stack>
                <Stack direction="row">
                    <Box width="65px">Last sync:</Box>
                    <Box>
                        {
                            isDataUpdating
                                ? "Updating..."
                                : lastDataUpdateTimestamp !== null
                                    ? dateTimeConversionService.convertDateToLongFormattedString(lastDataUpdateTimestamp) ?? "N/A"
                                    : "Failed to update..."
                        }
                    </Box>
                </Stack>
            </Stack>

            <Stack direction="column" sx={connectionColumnSx}>
                <StreamDataHealthInfo connections={streamConnectionDetails} />
            </Stack>
        </Stack>
    );
}

const connectionColumnSx = {
    fontWeight: "500",
    minWidth: '118px',
};

const connectionRowSx = {
    alignItems: 'center',
    columnGap: '5px',
    minHeight: '18px',
};

function StreamDataHealthInfo({
    connections,
}: {
    connections: StreamConnectionDetail[];
}) {
    return (
        <Tooltip
            title={<StreamDataTooltip connections={connections} />}
            slotProps={{
                tooltip: {
                    sx: {
                        // backgroundColor: 'rgba(50,50,50,0.95)',
                        // color: 'rgba(200,200,200,1)',
                        fontSize: 12
                    }
                }
            }}
        >
            <Stack
                direction="row"
                sx={{
                    ...connectionRowSx,
                    cursor: 'default',
                }}
                alignItems="flex-end"
            >
                <StatusCircle
                    isOpen={false}
                    color={getStreamDataStatusColor(connections)}
                    size={10}
                />
                <Box>Stream data</Box>
            </Stack>
        </Tooltip>
    );
}

function StreamDataTooltip({
    connections,
}: {
    connections: StreamConnectionDetail[];
}) {
    return (
        <Stack direction="column" sx={{ fontSize: "13px" }}>
            <Stack
                direction="row"
                sx={{
                    alignItems: 'center',
                    columnGap: '12px',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                }}
            >
                <Box width="150px">Stream state and name</Box>
                <Box width="75px" sx={{ textAlign: 'right' }}>Updated at</Box>
            </Stack>
            <Divider sx={{ paddingTop: "5px", marginBottom: "5px", borderColor: '#f2f3f3' }} />
            {connections.map(connection => (
                <Stack
                    key={connection.name}
                    direction="row"
                    sx={{
                        alignItems: 'center',
                        columnGap: '12px',
                        whiteSpace: 'nowrap',
                    }}
                >
                    <Stack
                        direction="row"
                        sx={{
                            alignItems: 'center',
                            columnGap: '6px',
                            width: '160px',
                        }}
                    >
                        <StatusCircle isOpen={connection.isOpen} size={8} />
                        <Box>{connection.name}</Box>
                    </Stack>
                    <Box width='75px' sx={{ textAlign: 'right' }}>
                        {formatLastDataReceivedAt(connection.lastDataReceivedAt)}
                    </Box>
                </Stack>
            ))}
        </Stack>
    );
}

function StatusCircle({
    isOpen,
    size,
    color,
}: {
    isOpen: boolean;
    size?: number;
    color?: string;
}) {
    return (
        <Box
            component="span"
            sx={{
                width: size,
                height: size,
                minWidth: size,
                borderRadius: '50%',
                backgroundColor: color ?? getConnectionStatusColor(isOpen),
                display: 'block',
                flexShrink: 0,
            }}
        />
    );
}

function formatLastDataReceivedAt(value: Date | string | null | undefined): string {
    if (value == null) {
        return "Unknown";
    }

    const date = value instanceof Date
        ? value
        : new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "Unknown";
    }

    return dateTimeConversionService.convertDateToCustomFormattedString(date, "HH:mm:ss.SSS") || "Unknown";
}

type StreamConnectionDetail = {
    name: string;
    isOpen: boolean;
    lastDataReceivedAt: Date | string | null | undefined;
};

function getStreamConnectionDetails(
    spotsConnection: WebSocketHealthStateDto | null,
    futuresConnection: WebSocketHealthStateDto | null,
    optionsConnection: WebSocketHealthStateDto | null,
    optionsOrderBookConnections: WebSocketHealthStateDto[],
    isOptionsOrderBookEnabled: boolean,
): StreamConnectionDetail[] {
    const optionsOrderBookDetails = !isOptionsOrderBookEnabled
        ? []
        : optionsOrderBookConnections.length === 0
            ? [createStreamConnectionDetail("Options Order Book", null)]
            : optionsOrderBookConnections.map((connection, index) =>
                createStreamConnectionDetail(`Options Order Book ${index + 1}`, connection));

    return [
        createStreamConnectionDetail("Spots", spotsConnection),
        createStreamConnectionDetail("Futures", futuresConnection),
        createStreamConnectionDetail("Options", optionsConnection),
        ...optionsOrderBookDetails,
    ];
}

function createStreamConnectionDetail(
    name: string,
    connection: WebSocketHealthStateDto | null,
): StreamConnectionDetail {
    return {
        name,
        isOpen: connection?.IsOpen === true,
        lastDataReceivedAt: connection?.LastDataReceivedAt,
    };
}

function getStreamDataStatusColor(connections: StreamConnectionDetail[]): string {
    if (connections.every(x => !x.isOpen)) {
        return statusClosedColor;
    }

    if (connections.every(x => x.isOpen)) {
        return statusOpenColor;
    }

    return statusPartiallyOpenColor;
}

function getConnectionStatusColor(isOpen: boolean): string {
    return isOpen ? statusOpenColor : statusClosedColor;
}

function getSortedOptionsOrderBookConnections(
    connections: Record<string, WebSocketHealthStateDto> | null,
): WebSocketHealthStateDto[] {
    return Object
        .values(connections ?? {})
        .sort((left, right) => {
            const leftIndex = getTrailingConnectionIndex(left.ConnectionName);
            const rightIndex = getTrailingConnectionIndex(right.ConnectionName);

            if (leftIndex !== null && rightIndex !== null && leftIndex !== rightIndex) {
                return leftIndex - rightIndex;
            }

            return left.ConnectionName.localeCompare(right.ConnectionName);
        });
}

function getTrailingConnectionIndex(connectionName: string): number | null {
    const match = connectionName.match(/_(\d+)$/);

    return match === null
        ? null
        : Number(match[1]);
}

const statusOpenColor = '#2ebd59';
const statusClosedColor = '#ff5050';
const statusPartiallyOpenColor = '#f5a623';

function getDataProviderName(dataProvider: DataProviders | null): string {
    const unknownDataProviderName = "Unknown";
    
    if (dataProvider === null)
        return unknownDataProviderName;
    
    const isNoData = (dataProvider & DataProviders.None) === DataProviders.None;
    const isTestDataProviderUsed = (dataProvider & DataProviders.Snapshot) === DataProviders.Snapshot;
    const isBinanceDataProviderUsed = (dataProvider & DataProviders.Binance) === DataProviders.Binance;
    
    return isTestDataProviderUsed
        ? "Snapshot (Binance)"
        : isBinanceDataProviderUsed
            ? "Binance"
            : isNoData
                ? "No data"
                : unknownDataProviderName;
} 
